/**
 * CommunicationHistory Component
 * Displays SMS conversation history in a chat-like interface
 */

import { useState, useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  MessageSquare,
  Send,
  Phone,
  User,
  Clock,
  CheckCheck,
  Check,
  AlertCircle,
  Loader2,
  RefreshCw,
  ChevronDown,
  FileText,
  Link,
  X,
  ExternalLink,
  Leaf,
  Info
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { format } from 'date-fns'
import { sv } from 'date-fns/locale'
import toast from 'react-hot-toast'
import {
  getConversation,
  sendSMS,
  markAsRead,
  subscribeToMessages,
  linkCommunicationToAssignment,
  dismissLinkSuggestion
} from '../services/sms'
import { getTemplates } from '../services/smsTemplates'
import { useAuth } from '../lib/auth'
import { TEMPLATE_VARIABLES } from './SendSMSModal'
import { getAssignmentStatusBadgeClassesV2, ASSIGNMENT_STATUSES_V2 } from '../services/statusSystemV2'
import type { AssignmentStatusV2 } from '../types/statusTypes'
import type { Communication, RecipientType } from '../types/communication'

interface CommunicationHistoryProps {
  recipientType: RecipientType
  recipientId: string
  recipientName: string
  recipientPhone: string
  assignmentId?: string
  className?: string
  hideHeader?: boolean
}

interface Assignment {
  id: string
  title: string
  assignment_number: number
  status: string
}

export function CommunicationHistory({
  recipientType,
  recipientId,
  recipientName,
  recipientPhone,
  assignmentId,
  className = '',
  hideHeader = false
}: CommunicationHistoryProps) {
  const { profile } = useAuth()
  const [newMessage, setNewMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [linkingMessageId, setLinkingMessageId] = useState<string | null>(null)
  const [recipientAssignments, setRecipientAssignments] = useState<Assignment[]>([])
  const [showTemplates, setShowTemplates] = useState(false)
  const [showVariables, setShowVariables] = useState(false)
  const [initialUnreadIds, setInitialUnreadIds] = useState<Set<string>>(new Set())
  const hasStoredInitialUnread = useRef(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Fetch templates
  const { data: templates = [] } = useQuery({
    queryKey: ['sms-templates', recipientType],
    queryFn: () => getTemplates(recipientType)
  })

  // Fetch conversation history
  const { data: messages = [], isLoading, error, refetch } = useQuery({
    queryKey: ['conversation', recipientType, recipientId],
    queryFn: () => getConversation(recipientType, recipientId),
    refetchInterval: 30000 // Refetch every 30 seconds
  })

  // Subscribe to real-time updates
  useEffect(() => {
    const unsubscribe = subscribeToMessages((message) => {
      // If this message is for this conversation, refetch
      if (
        message.recipient_type === recipientType &&
        message.recipient_id === recipientId
      ) {
        queryClient.invalidateQueries({ 
          queryKey: ['conversation', recipientType, recipientId] 
        })
      }
    })

    return () => unsubscribe()
  }, [recipientType, recipientId, queryClient])

  // Store initial unread message IDs before marking as read
  useEffect(() => {
    if (messages.length > 0 && !hasStoredInitialUnread.current) {
      const unreadIds = new Set(
        messages
          .filter(m => m.direction === 'inbound' && !m.read_at)
          .map(m => m.id)
      )
      setInitialUnreadIds(unreadIds)
      hasStoredInitialUnread.current = true

      // Mark as read after a short delay to let the UI show the indicators
      setTimeout(() => {
        markAsRead(recipientType, recipientId)
      }, 500)
    }
  }, [messages, recipientType, recipientId])

  // Reset when recipient changes
  useEffect(() => {
    hasStoredInitialUnread.current = false
    setInitialUnreadIds(new Set())
  }, [recipientType, recipientId])

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Fetch assignments on mount (for suggestions and linking)
  useEffect(() => {
    const fetchAssignments = async () => {
      if (recipientType === 'customer') {
        // Fetch customer's assignments directly
        const { data } = await supabase
          .from('assignments')
          .select('id, title, assignment_number, status')
          .eq('customer_id', recipientId)
          .not('status', 'in', '("slutford","avbrutten")')
          .order('assignment_number', { ascending: false })
          .limit(20)
        setRecipientAssignments(data || [])
      } else if (recipientType === 'subcontractor') {
        // Fetch subcontractor's assignments via junction table
        const { data } = await supabase
          .from('assignment_subcontractors')
          .select(`
            assignment_id,
            assignments:assignment_id (
              id,
              title,
              assignment_number,
              status
            )
          `)
          .eq('subcontractor_id', recipientId)
          .limit(20)

        // Extract assignments from the joined data and filter out completed/cancelled
        const assignments: Assignment[] = []
        for (const row of data || []) {
          // Cast through unknown due to Supabase typing
          const a = row.assignments as unknown as { id: string; title: string; assignment_number: number; status: string } | null
          if (a && a.status !== 'slutford' && a.status !== 'avbrutten') {
            assignments.push({
              id: a.id,
              title: a.title,
              assignment_number: a.assignment_number,
              status: a.status
            })
          }
        }
        assignments.sort((a, b) => b.assignment_number - a.assignment_number)

        setRecipientAssignments(assignments)
      }
    }
    fetchAssignments()
  }, [recipientType, recipientId])

  // Check for unlinked inbound messages (exclude dismissed)
  const unlinkedInboundMessages = messages.filter(
    m => m.direction === 'inbound' && !m.assignment_id && !m.link_dismissed
  )

  // Handle dismissing link suggestion
  const handleDismissLink = async (messageId: string) => {
    const result = await dismissLinkSuggestion(messageId)
    if (result.success) {
      toast.success('Markerat som ej kopplat')
      refetch()
    } else {
      toast.error('Kunde inte uppdatera')
    }
    setLinkingMessageId(null)
  }

  // Handle linking a message to an assignment
  const handleLinkAssignment = async (messageId: string, targetAssignmentId: string | null) => {
    const result = await linkCommunicationToAssignment(messageId, targetAssignmentId)
    if (result.success) {
      toast.success(targetAssignmentId ? 'Kopplat till uppdrag' : 'Koppling borttagen')
      refetch()
      // Also invalidate assignment-communications and unread count queries
      if (targetAssignmentId) {
        // Use partial matching to invalidate all assignment-communications queries for this assignment
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'assignment-communications' &&
            query.queryKey[1] === targetAssignmentId
        })
        queryClient.invalidateQueries({ queryKey: ['assignment-unread-count', targetAssignmentId] })
      }
    } else {
      toast.error('Kunde inte uppdatera koppling')
    }
    setLinkingMessageId(null)
  }

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return

    if (!recipientPhone) {
      toast.error('Mottagaren har inget telefonnummer')
      return
    }

    setIsSending(true)

    try {
      // Replace any variables before sending
      const finalMessage = replaceVariables(newMessage.trim())

      const result = await sendSMS({
        recipient_type: recipientType,
        recipient_id: recipientId,
        recipient_name: recipientName,
        recipient_phone: recipientPhone,
        message: finalMessage,
        assignment_id: assignmentId
      })

      if (result.success) {
        setNewMessage('')
        refetch()
      } else {
        toast.error(result.error || 'Kunde inte skicka meddelande')
      }
    } catch (error) {
      toast.error('Ett fel uppstod')
    } finally {
      setIsSending(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  // Replace template variables with actual values
  const replaceVariables = (text: string): string => {
    let result = text
    const firstName = recipientName.split(' ')[0]
    result = result.replace(/{name}/g, firstName)
    result = result.replace(/{fullname}/g, recipientName)

    // Sender name
    const senderFirstName = profile?.full_name?.split(' ')[0] || ''
    result = result.replace(/{avsändare}/g, senderFirstName)

    // Clear assignment variables (not available in this context without assignmentId)
    result = result.replace(/{uppdragsnr}/g, '')
    result = result.replace(/{datum}/g, '')
    result = result.replace(/{tid}/g, '')
    result = result.replace(/{location}/g, '')
    result = result.replace(/{titel}/g, '')
    result = result.replace(/{trädgårdsmästare}/g, '')

    return result
  }

  // Handle template selection
  const handleSelectTemplate = (templateContent: string) => {
    const text = replaceVariables(templateContent)
    setNewMessage(text)
    setShowTemplates(false)
  }

  const getStatusIcon = (message: Communication) => {
    switch (message.status) {
      case 'delivered':
        return <CheckCheck className="h-3 w-3 text-blue-500" />
      case 'sent':
        return <Check className="h-3 w-3 text-nature-400" />
      case 'failed':
        return <AlertCircle className="h-3 w-3 text-red-500" />
      case 'pending':
        return <Clock className="h-3 w-3 text-nature-400" />
      default:
        return null
    }
  }


  if (isLoading) {
    return (
      <div className={`flex items-center justify-center h-64 ${className}`}>
        <Loader2 className="h-8 w-8 animate-spin text-nature-400" />
      </div>
    )
  }

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center h-64 ${className}`}>
        <AlertCircle className="h-8 w-8 text-red-500 mb-2" />
        <p className="text-nature-600 dark:text-nature-400">
          Kunde inte ladda konversation
        </p>
        <button
          onClick={() => refetch()}
          className="mt-2 text-sm text-blue-500 hover:underline"
        >
          Försök igen
        </button>
      </div>
    )
  }

  return (
    <div className={`flex flex-col h-full bg-[#faf9f7] dark:bg-nature-800 rounded-lg border border-nature-200 dark:border-nature-700 ${className}`}>
      {/* Header - hidden when embedded in slide-over */}
      {!hideHeader && (
        <div className="flex-shrink-0 px-4 py-3 border-b border-nature-200 dark:border-nature-700 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-full ${
              recipientType === 'customer'
                ? 'bg-blue-100 dark:bg-blue-900'
                : 'bg-green-100 dark:bg-green-900'
            }`}>
              {recipientType === 'customer' ? (
                <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              ) : (
                <Leaf className="h-4 w-4 text-green-600 dark:text-green-400" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-medium text-nature-900 dark:text-nature-100">
                  {recipientName}
                </h4>
                <button
                  onClick={() => navigate(
                    recipientType === 'customer'
                      ? `/admin/customers/${recipientId}`
                      : `/admin/subcontractors/${recipientId}`
                  )}
                  className="p-1 text-nature-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  title={recipientType === 'customer' ? 'Visa kund' : 'Visa trädgårdsmästare'}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="flex items-center space-x-2 text-sm text-nature-500 dark:text-nature-400">
                {recipientPhone && (
                  <>
                    <Phone className="h-3 w-3" />
                    <span>{recipientPhone}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="p-2 text-nature-400 hover:text-nature-600 dark:hover:text-nature-300 transition-colors"
            title="Uppdatera"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Suggestion banner for unlinked messages - compact */}
      {unlinkedInboundMessages.length > 0 && recipientAssignments.length > 0 && (
        <div className="flex-shrink-0 mx-2 sm:mx-4 mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1">
              <Link className="h-3 w-3" />
              {unlinkedInboundMessages.length} ej kopplat:
            </span>
            {recipientAssignments.slice(0, 2).map(assignment => (
              <button
                key={assignment.id}
                onClick={async () => {
                  for (const msg of unlinkedInboundMessages) {
                    await linkCommunicationToAssignment(msg.id, assignment.id)
                  }
                  toast.success(`Kopplat till #${assignment.assignment_number}`)
                  refetch()
                  queryClient.invalidateQueries({
                    predicate: (query) =>
                      query.queryKey[0] === 'assignment-communications' &&
                      query.queryKey[1] === assignment.id
                  })
                  queryClient.invalidateQueries({ queryKey: ['assignment-unread-count', assignment.id] })
                }}
                className="text-xs px-1.5 py-0.5 bg-amber-100 dark:bg-amber-800/50 hover:bg-amber-200 dark:hover:bg-amber-700/50 text-amber-800 dark:text-amber-200 rounded transition-colors"
              >
                #{assignment.assignment_number}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-8">
            <MessageSquare className="h-12 w-12 text-nature-300 dark:text-nature-600 mb-3" />
            <p className="text-nature-500 dark:text-nature-400 text-center">
              Ingen konversation ännu
            </p>
            <p className="text-sm text-nature-400 dark:text-nature-500 text-center mt-1">
              Skicka ett SMS för att starta en konversation
            </p>
          </div>
        ) : (
          messages.map((message, index) => {
            // Check if this message was unread when we opened the conversation
            const isUnread = initialUnreadIds.has(message.id)
            const isFirstUnread = isUnread &&
              !messages.slice(0, index).some(m => initialUnreadIds.has(m.id))

            return (
              <div key={message.id}>
                {/* Unread messages divider */}
                {isFirstUnread && (
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1 h-px bg-red-400"></div>
                    <span className="text-xs font-medium text-red-500 dark:text-red-400 uppercase tracking-wide">
                      Nya meddelanden
                    </span>
                    <div className="flex-1 h-px bg-red-400"></div>
                  </div>
                )}
                <div
                  className={`flex ${
                    message.direction === 'outbound' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div className="relative group">
                    {/* Unread indicator dot */}
                    {isUnread && (
                      <div className="absolute -left-2 top-2 w-2 h-2 bg-red-500 rounded-full"></div>
                    )}
                    <div
                      className={`max-w-[80%] rounded-lg px-4 py-2 ${
                        message.direction === 'outbound'
                          ? 'bg-blue-500 text-white'
                          : isUnread
                            ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-nature-900 dark:text-nature-100'
                            : 'bg-nature-100 dark:bg-nature-700 text-nature-900 dark:text-nature-100'
                      }`}
                    >
                  {/* Assignment link badge or link button */}
                  {message.assignments ? (
                    <div className="flex items-center gap-1 mb-1.5">
                      <button
                        onClick={() => navigate(`/admin/assignments/${message.assignments!.id}`)}
                        className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded transition-colors ${
                          message.direction === 'outbound'
                            ? 'bg-blue-400/50 hover:bg-blue-400/70 text-white'
                            : 'bg-primary-100 dark:bg-primary-900/30 hover:bg-primary-200 dark:hover:bg-primary-900/50 text-primary-700 dark:text-primary-300'
                        }`}
                      >
                        <FileText className="h-3 w-3" />
                        <span>#{message.assignments.assignment_number} {message.assignments.title}</span>
                      </button>
                      <button
                        onClick={() => handleLinkAssignment(message.id, null)}
                        className={`p-0.5 rounded transition-colors ${
                          message.direction === 'outbound'
                            ? 'hover:bg-blue-400/50 text-blue-200'
                            : 'hover:bg-nature-200 dark:hover:bg-nature-600 text-nature-400'
                        }`}
                        title="Ta bort koppling"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : message.link_dismissed ? (
                    <div className="flex items-center gap-1 mb-1.5">
                      <span
                        className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${
                          message.direction === 'outbound'
                            ? 'bg-blue-400/30 text-blue-100'
                            : 'bg-nature-200 dark:bg-nature-600 text-nature-500 dark:text-nature-400'
                        }`}
                      >
                        <X className="h-3 w-3" />
                        <span>Ej kopplat</span>
                      </span>
                      {recipientAssignments.length > 0 && (
                        <button
                          onClick={() => setLinkingMessageId(linkingMessageId === message.id ? null : message.id)}
                          className={`p-0.5 rounded transition-colors ${
                            message.direction === 'outbound'
                              ? 'hover:bg-blue-400/50 text-blue-200'
                              : 'hover:bg-nature-200 dark:hover:bg-nature-600 text-nature-400'
                          }`}
                          title="Koppla till uppdrag"
                        >
                          <Link className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  ) : recipientAssignments.length > 0 && (
                    <button
                      onClick={() => setLinkingMessageId(linkingMessageId === message.id ? null : message.id)}
                      className={`inline-flex items-center gap-1 text-xs mb-1.5 px-2 py-0.5 rounded transition-colors ${
                        message.direction === 'outbound'
                          ? 'bg-blue-400/30 hover:bg-blue-400/50 text-blue-100'
                          : 'bg-nature-200 dark:bg-nature-600 hover:bg-nature-300 dark:hover:bg-nature-500 text-nature-600 dark:text-nature-300'
                      }`}
                    >
                      <Link className="h-3 w-3" />
                      <span>Koppla meddelande</span>
                    </button>
                  )}
                  <p className="text-sm whitespace-pre-wrap break-words">
                    {message.message}
                  </p>
                  <div
                    className={`flex items-center justify-end space-x-1 mt-1 text-xs ${
                      message.direction === 'outbound'
                        ? 'text-blue-100'
                        : 'text-nature-500 dark:text-nature-400'
                    }`}
                  >
                    <span>
                      {format(new Date(message.created_at), 'HH:mm', { locale: sv })}
                    </span>
                    {message.direction === 'outbound' && getStatusIcon(message)}
                  </div>
                </div>

                {/* Assignment linking dropdown - positioned based on message direction */}
                {linkingMessageId === message.id && (
                  <div className={`absolute z-50 mt-1 w-96 bg-white dark:bg-nature-800 rounded-lg shadow-lg border border-nature-200 dark:border-nature-700 py-1 max-h-48 overflow-y-auto ${
                    message.direction === 'outbound' ? 'right-0' : 'left-0'
                  }`}>
                    {/* Option for no assignment link */}
                    <button
                      onClick={() => handleDismissLink(message.id)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-nature-50 dark:hover:bg-nature-700 flex items-center gap-2 border-b border-nature-100 dark:border-nature-700"
                    >
                      <X className="h-3 w-3 text-nature-400" />
                      <span className="text-nature-600 dark:text-nature-400 italic">Ej kopplat till uppdrag</span>
                    </button>
                    {recipientAssignments.length === 0 ? (
                      <p className="px-3 py-2 text-sm text-nature-500">Inga uppdrag hittades</p>
                    ) : (
                      recipientAssignments.map(assignment => {
                        const statusInfo = ASSIGNMENT_STATUSES_V2.find(s => s.key === assignment.status)
                        return (
                          <button
                            key={assignment.id}
                            onClick={() => handleLinkAssignment(message.id, assignment.id)}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-nature-50 dark:hover:bg-nature-700 flex items-center gap-2"
                          >
                            <span className="text-nature-400 font-mono">#{assignment.assignment_number}</span>
                            <span className="text-nature-900 dark:text-nature-100 truncate flex-1">{assignment.title}</span>
                            {statusInfo && (
                              <span className={`px-1.5 py-0.5 text-xs rounded ${getAssignmentStatusBadgeClassesV2(assignment.status as AssignmentStatusV2)}`}>
                                {statusInfo.name}
                              </span>
                            )}
                          </button>
                        )
                      })
                    )}
                    </div>
                  )}
                  </div>
                </div>
              </div>
            )
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Failed messages alert */}
      {messages.some(m => m.status === 'failed') && (
        <div className="flex-shrink-0 mx-4 mb-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-300 flex items-center space-x-2">
            <AlertCircle className="h-4 w-4" />
            <span>Vissa meddelanden kunde inte levereras</span>
          </p>
        </div>
      )}

      {/* Input area */}
      <div className="flex-shrink-0 px-4 py-3 border-t border-nature-200 dark:border-nature-700">
        {!recipientPhone ? (
          <div className="text-center py-2">
            <p className="text-sm text-nature-500 dark:text-nature-400">
              <Phone className="h-4 w-4 inline mr-1" />
              Lägg till ett telefonnummer för att skicka SMS
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Template and variable selectors */}
            <div className="flex items-center gap-4">
              {templates.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => { setShowTemplates(!showTemplates); setShowVariables(false) }}
                    className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    <FileText className="h-3 w-3" />
                    <span>Välj mall</span>
                    <ChevronDown className={`h-3 w-3 transition-transform ${showTemplates ? 'rotate-180' : ''}`} />
                  </button>

                  {showTemplates && (
                    <div className="absolute bottom-full left-0 mb-1 w-64 bg-white dark:bg-nature-800 rounded-lg shadow-lg border border-nature-200 dark:border-nature-700 py-1 max-h-48 overflow-y-auto z-10">
                      {templates.map(template => (
                        <button
                          key={template.id}
                          onClick={() => handleSelectTemplate(template.content)}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-nature-50 dark:hover:bg-nature-700"
                        >
                          <span className="font-medium text-nature-900 dark:text-nature-100">{template.name}</span>
                          <p className="text-xs text-nature-500 dark:text-nature-400 truncate mt-0.5">{template.content}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={() => { setShowVariables(!showVariables); setShowTemplates(false) }}
                className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
              >
                <Info className="h-3 w-3" />
                <span>Variabler</span>
              </button>
            </div>

            {/* Variables help panel */}
            {showVariables && (
              <div className="p-3 bg-nature-50 dark:bg-nature-700 rounded-lg border border-nature-200 dark:border-nature-600">
                <p className="text-xs font-medium text-nature-700 dark:text-nature-300 mb-2">
                  Tillgängliga variabler:
                </p>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  {TEMPLATE_VARIABLES.filter(v => v.always).map((v) => (
                    <div key={v.key} className="flex items-center gap-2">
                      <code className="px-1 py-0.5 bg-nature-200 dark:bg-nature-600 rounded text-nature-800 dark:text-nature-200">
                        {v.key}
                      </code>
                      <span className="text-nature-500 dark:text-nature-400 truncate">{v.description}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-nature-500 dark:text-nature-400 mt-2 italic">
                  Uppdragsvariabler tillgängliga via uppdragssidan
                </p>
              </div>
            )}

            <div className="flex items-end space-x-2">
              <div className="flex-1">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Skriv ett meddelande..."
                  rows={2}
                  className="input-field resize-none text-sm"
                  disabled={isSending}
                />
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-nature-400">
                    {newMessage.length} / 160 tecken
                    {newMessage.length > 160 && (
                      <span className="text-yellow-500 ml-1">
                        ({Math.ceil(newMessage.length / 160)} SMS)
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-nature-400">
                    Tryck Enter för att skicka
                  </span>
                </div>
              </div>
              <button
                onClick={handleSendMessage}
                disabled={isSending || !newMessage.trim()}
                className="btn-primary p-3 rounded-lg"
              >
                {isSending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Compact version for sidebars or cards
 */
export function CommunicationHistoryCompact({
  recipientType,
  recipientId,
  onExpand
}: Omit<CommunicationHistoryProps, 'recipientName' | 'recipientPhone'> & { onExpand?: () => void }) {
  const { data: messages = [], isLoading } = useQuery({
    queryKey: ['conversation', recipientType, recipientId],
    queryFn: () => getConversation(recipientType, recipientId)
  })

  const unreadCount = messages.filter(
    m => m.direction === 'inbound' && !m.read_at
  ).length

  const lastMessage = messages[messages.length - 1]

  return (
    <button
      onClick={onExpand}
      className="w-full p-3 bg-nature-50 dark:bg-nature-700 rounded-lg hover:bg-nature-100 dark:hover:bg-nature-600 transition-colors text-left"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-full">
              <MessageSquare className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </div>
          <div>
            <p className="font-medium text-nature-900 dark:text-nature-100 text-sm">
              SMS-historik
            </p>
            {isLoading ? (
              <p className="text-xs text-nature-500">Laddar...</p>
            ) : lastMessage ? (
              <p className="text-xs text-nature-500 dark:text-nature-400 truncate max-w-[200px]">
                {lastMessage.direction === 'outbound' ? 'Du: ' : ''}
                {lastMessage.message}
              </p>
            ) : (
              <p className="text-xs text-nature-500">Inga meddelanden</p>
            )}
          </div>
        </div>
        <ChevronDown className="h-4 w-4 text-nature-400" />
      </div>
    </button>
  )
}

